import React, { useState } from 'react';
import { cn } from './cn';
import { Icon } from './base/Icon';
import { usePersistentState } from './usePersistentState';

/**
 * FilterBar — ตัวกรองหลายชั้น (FR-ADM-01 ระบุตัวกรอง 7 ชนิดในหน้าเดียว)
 *
 * หลักการ: ตัวกรองที่ใช้อยู่ต้อง "อ่านออกเป็นประโยค" และลบทีละอันได้
 * ชุดตัวกรองที่ใช้บ่อยบันทึกไว้ได้ (Saved views) และจำต่อผู้ใช้
 */

export interface ActiveFilter {
  /** id ของตัวกรอง เช่น 'status' */
  id: string;
  /** ชื่อที่แสดงบนชิป เช่น 'สถานะ' */
  field: string;
  /** ค่าที่แสดง เช่น 'รอตรวจสอบ, กำลังตรวจ' */
  display: string;
}

export interface SavedView {
  id: string;
  name: string;
  /** ค่าตัวกรองดิบ — รูปแบบขึ้นกับหน้าจอที่ใช้ */
  filters: Record<string, unknown>;
}

export interface FilterBarProps {
  /** ตัวควบคุมตัวกรอง (dropdown, ช่วงวันที่, ช่วงตัวเลข) วางเรียงกันในแถวแรก */
  children?: React.ReactNode;
  active: ActiveFilter[];
  onRemove: (id: string) => void;
  onClearAll: () => void;
  /** ค่าตัวกรองปัจจุบันสำหรับบันทึกเป็น Saved view */
  currentFilters?: Record<string, unknown>;
  onApplyView?: (view: SavedView) => void;
  /** คีย์สำหรับจำ Saved views ต่อหน้าจอ เช่น 'claims-queue' */
  persistKey?: string;
  /** จำนวนผลลัพธ์ที่ตรงตัวกรอง — แสดงต่อท้ายเพื่อให้รู้ผลก่อนเลื่อนดู */
  resultCount?: number;
  className?: string;
}

export const FilterBar: React.FC<FilterBarProps> = ({
  children, active, onRemove, onClearAll,
  currentFilters, onApplyView, persistKey = 'default', resultCount, className,
}) => {
  const [views, setViews] = usePersistentState<SavedView[]>(`views.${persistKey}`, []);
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState('');

  const save = () => {
    const trimmed = name.trim();
    if (!trimmed || !currentFilters) return;
    setViews([...views, { id: `${Date.now()}`, name: trimmed, filters: currentFilters }]);
    setName('');
    setNaming(false);
  };

  return (
    <section
      aria-label="ตัวกรอง"
      className={cn(
        'flex flex-col gap-[var(--space-2)] border-b border-[var(--line-200)] bg-[var(--surface-0)] px-[var(--space-4)] py-[var(--space-25)] font-[family-name:var(--font-thai)]',
        className,
      )}
    >
      <div className="flex flex-wrap items-center gap-[var(--space-2)]">
        {children}

        {views.length > 0 && (
          <label className="ml-auto flex items-center gap-[var(--space-2)] text-[length:var(--text-form)] text-[var(--ink-700)]">
            <Icon name="bookmark" size={16} className="text-[var(--ink-600)]" />
            มุมมองที่บันทึกไว้
            <select
              defaultValue=""
              onChange={(e) => {
                const v = views.find((x) => x.id === e.target.value);
                if (v && onApplyView) onApplyView(v);
              }}
              className="min-h-[var(--hit-control)] rounded-[var(--radius-sm)] border border-[var(--line-300)] bg-[var(--surface-0)] px-[var(--space-2)] text-[length:var(--text-form)] text-[var(--ink-900)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2"
            >
              <option value="" disabled>เลือกมุมมอง</option>
              {views.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </label>
        )}
      </div>

      {(active.length > 0 || resultCount !== undefined) && (
        <div className="flex flex-wrap items-center gap-[var(--space-2)]">
          {active.length > 0 && (
            <span className="text-[length:var(--text-table-sm)] text-[var(--ink-600)]">กรองด้วย</span>
          )}

          {active.map((f) => (
            <span
              key={f.id}
              className="inline-flex items-center gap-[var(--space-1)] rounded-[var(--radius-pill)] border border-[var(--blue-200)] bg-[var(--blue-050)] py-[var(--space-05)] pl-[var(--space-25)] pr-[var(--space-1)] text-[length:var(--text-table-sm)] leading-[var(--leading-table)] text-[var(--blue-900)]"
            >
              <span className="bo-truncate max-w-[280px]">
                <span className="text-[var(--ink-600)]">{f.field}:</span>{' '}
                <span className="font-[var(--weight-medium)]">{f.display}</span>
              </span>
              <button
                type="button"
                aria-label={`ลบตัวกรอง ${f.field}`}
                onClick={() => onRemove(f.id)}
                className="inline-flex h-[22px] w-[22px] items-center justify-center rounded-full text-[var(--blue-700)] hover:bg-[var(--blue-200)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
              >
                <Icon name="close" size={14} />
              </button>
            </span>
          ))}

          {active.length > 0 && (
            <button
              type="button"
              onClick={onClearAll}
              className="min-h-[var(--hit-table)] rounded-[var(--radius-sm)] px-[var(--space-2)] text-[length:var(--text-table-sm)] font-[var(--weight-medium)] text-[var(--blue-700)] underline underline-offset-2 hover:text-[var(--blue-800)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2"
            >
              ล้างตัวกรองทั้งหมด
            </button>
          )}

          {active.length > 0 && currentFilters && (
            naming ? (
              <span className="flex items-center gap-[var(--space-1)]">
                <input
                  autoFocus
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setNaming(false); }}
                  placeholder="ชื่อมุมมอง"
                  aria-label="ชื่อมุมมองที่จะบันทึก"
                  className="min-h-[var(--hit-table)] w-[180px] rounded-[var(--radius-sm)] border border-[var(--line-300)] px-[var(--space-2)] text-[length:var(--text-table-sm)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
                />
                <button
                  type="button"
                  onClick={save}
                  className="min-h-[var(--hit-table)] rounded-[var(--radius-sm)] bg-[var(--blue-700)] px-[var(--space-25)] text-[length:var(--text-table-sm)] font-[var(--weight-medium)] text-white outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2"
                >
                  บันทึก
                </button>
              </span>
            ) : (
              <button
                type="button"
                onClick={() => setNaming(true)}
                className="inline-flex min-h-[var(--hit-table)] items-center gap-[var(--space-1)] rounded-[var(--radius-sm)] px-[var(--space-2)] text-[length:var(--text-table-sm)] font-[var(--weight-medium)] text-[var(--ink-700)] hover:bg-[var(--surface-100)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2"
              >
                <Icon name="bookmark_add" size={16} />
                บันทึกเป็นมุมมอง
              </button>
            )
          )}

          {resultCount !== undefined && (
            <span aria-live="polite" className="ml-auto text-[length:var(--text-table-sm)] text-[var(--ink-700)]">
              พบ <span className="bo-num font-[var(--weight-semibold)]">{resultCount.toLocaleString('th-TH')}</span> รายการ
            </span>
          )}
        </div>
      )}
    </section>
  );
};
